import {getAuthData} from '@utils/auth.js'
import {BUILD_VERSION} from './buildInfo.js'
import {getRuntimeMode} from './appRuntime.js'
import {registerAppServiceWorker} from './serviceWorker.js'

const API_URL = import.meta.env.VITE_API_URL
const PUSH_INSTALL_ID_KEY = 'push_install_id'
const PUSH_LAST_SYNC_KEY = 'push_last_sync_v1'
const DEFAULT_SYNC_THROTTLE_MS = 12 * 60 * 60 * 1000

function getToken() {
  return getAuthData()?.token || null
}

function getCurrentUserKey() {
  const auth = getAuthData()
  const candidate = auth?.user?._id || auth?.user?.id || auth?.user?.email || null
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : 'unknown-user'
}

function buildAuthHeaders(includeJson = false) {
  const headers = {}
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  if (includeJson) {
    headers['Content-Type'] = 'application/json'
  }
  return headers
}

function resolveApiUrl(path) {
  const base = typeof API_URL === 'string' ? API_URL.replace(/\/$/, '') : ''
  return `${base}${path}`
}

function generateInstallId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return `push-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

function getPushInstallId() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const stored = window.localStorage.getItem(PUSH_INSTALL_ID_KEY)
    if (typeof stored === 'string' && stored.trim()) {
      return stored.trim()
    }

    const nextId = generateInstallId()
    window.localStorage.setItem(PUSH_INSTALL_ID_KEY, nextId)
    return nextId
  } catch {
    return null
  }
}

function readLastSync() {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(PUSH_LAST_SYNC_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeLastSync({status, endpoint = null} = {}) {
  if (typeof window === 'undefined') return

  const payload = {
    status: typeof status === 'string' ? status : 'unknown',
    endpoint: typeof endpoint === 'string' ? endpoint : null,
    userKey: getCurrentUserKey(),
    installId: getPushInstallId(),
    appVersion: BUILD_VERSION,
    runtimeMode: getRuntimeMode(),
    syncedAt: Date.now(),
  }

  try {
    window.localStorage.setItem(PUSH_LAST_SYNC_KEY, JSON.stringify(payload))
  } catch {
    // ignore persistence errors
  }
}

function shouldSkipSync({force = false, throttleMs = DEFAULT_SYNC_THROTTLE_MS} = {}) {
  if (force) return false
  if (!Number.isFinite(throttleMs) || throttleMs <= 0) return false

  const lastSync = readLastSync()
  if (!lastSync || !['granted', 'disabled'].includes(lastSync.status)) return false
  if (lastSync.userKey !== getCurrentUserKey()) return false
  if (lastSync.installId !== getPushInstallId()) return false
  if (lastSync.appVersion !== BUILD_VERSION) return false
  if (lastSync.runtimeMode !== getRuntimeMode()) return false

  const syncedAt = Number(lastSync.syncedAt)
  return Number.isFinite(syncedAt) && Date.now() - syncedAt < throttleMs
}

function supportsDeclarativeWebPush() {
  if (typeof window === 'undefined') {
    return false
  }

  return 'pushManager' in window
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const normalized = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(normalized)
  const output = new Uint8Array(rawData.length)

  for (let index = 0; index < rawData.length; index += 1) {
    output[index] = rawData.charCodeAt(index)
  }

  return output
}

async function parseResponseError(response) {
  try {
    const payload = await response.json()
    return payload?.error || payload?.message || 'Push request failed'
  } catch {
    return 'Push request failed'
  }
}

function isPushNotConfiguredError(message) {
  return typeof message === 'string' && /web push is not configured/i.test(message)
}

async function getReadyRegistration() {
  await registerAppServiceWorker()

  if (!('serviceWorker' in navigator)) {
    return null
  }

  const registration = await navigator.serviceWorker.ready
  return registration || null
}

async function fetchPublicKey() {
  const response = await fetch(resolveApiUrl('/v2/me/push-subscriptions/public-key'), {
    headers: buildAuthHeaders(),
  })

  if (!response.ok) {
    const error = await parseResponseError(response)
    if (response.status === 503 && isPushNotConfiguredError(error)) {
      return null
    }
    throw new Error(error)
  }

  const payload = await response.json()
  if (payload?.enabled === false) {
    return null
  }

  const publicKey = typeof payload?.publicKey === 'string' ? payload.publicKey.trim() : ''
  if (!publicKey) {
    throw new Error('Missing web push public key')
  }

  return publicKey
}

async function syncSubscriptionToBackend(subscription) {
  const serializedSubscription = typeof subscription?.toJSON === 'function'
    ? subscription.toJSON()
    : {
        endpoint: subscription?.endpoint || null,
        expirationTime: subscription?.expirationTime ?? null,
        keys: {
          auth: subscription?.getKey ? window.btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth') || []))) : null,
          p256dh: subscription?.getKey ? window.btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh') || []))) : null,
        },
      }

  const payload = {
    subscription: serializedSubscription,
    metadata: {
      runtimeMode: getRuntimeMode(),
      appVersion: BUILD_VERSION,
      deviceLabel: navigator.platform || null,
      userAgent: navigator.userAgent || null,
      permission: Notification.permission,
      installId: getPushInstallId(),
      capabilities: {
        declarativeWebPush: supportsDeclarativeWebPush(),
      },
    },
  }

  const response = await fetch(resolveApiUrl('/v2/me/push-subscriptions'), {
    method: 'POST',
    headers: buildAuthHeaders(true),
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(await parseResponseError(response))
  }

  return await response.json()
}

export function isWebPushSupported() {
  if (typeof window === 'undefined') return false
  if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return false
  }

  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export async function syncPushSubscription({requestPermission = false, force = false, throttleMs = DEFAULT_SYNC_THROTTLE_MS} = {}) {
  if (!isWebPushSupported()) {
    return {status: 'unsupported'}
  }

  if (!getToken()) {
    return {status: 'unauthorized'}
  }

  const registration = await getReadyRegistration()
  if (!registration?.pushManager) {
    return {status: 'unsupported'}
  }

  let permission = Notification.permission
  if (requestPermission && permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    return {status: permission}
  }

  if (shouldSkipSync({force, throttleMs})) {
    return {status: 'cached'}
  }

  const publicKey = await fetchPublicKey()
  if (!publicKey) {
    writeLastSync({status: 'disabled'})
    return {status: 'disabled'}
  }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
  }

  try {
    await syncSubscriptionToBackend(subscription)
  } catch (error) {
    if (isPushNotConfiguredError(error?.message)) {
      writeLastSync({status: 'disabled'})
      return {status: 'disabled'}
    }
    throw error
  }

  writeLastSync({status: 'granted', endpoint: subscription.endpoint})

  return {
    status: 'granted',
    endpoint: subscription.endpoint,
  }
}

export async function disablePushSubscription() {
  if (!isWebPushSupported()) {
    return {removed: 0, status: 'unsupported'}
  }

  if (!getToken()) {
    return {removed: 0, status: 'unauthorized'}
  }

  const registration = await getReadyRegistration()
  const subscription = await registration?.pushManager?.getSubscription()
  if (!subscription) {
    writeLastSync({status: 'disabled'})
    return {removed: 0, status: 'missing'}
  }

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  writeLastSync({status: 'disabled'})

  const response = await fetch(resolveApiUrl('/v2/me/push-subscriptions'), {
    method: 'DELETE',
    headers: buildAuthHeaders(true),
    body: JSON.stringify({endpoint}),
  })

  if (!response.ok) {
    throw new Error(await parseResponseError(response))
  }

  return await response.json()
}
